import { nanoid } from 'nanoid'

/* @ngInject */
function UsersController (
  $scope,
  $rootScope,
  $q,
  $state,
  $translate,
  $timeout,
  AdminService,
  ModalService,
  MessageService,
  SegmentService,
  UdsModalService,
  ToastService,
  Utils
) {
  const getEditUserDataFieldController = () => import('../../dialogs/edit-user-data-fields/edit-user-data-fields.controller')
  const getInviteTeamMembersDialogController = () =>
    $q.when(import('../../dialogs/invite-team-members/invite-team-members-dialog.controller'))
  const getChangePasswordDialogController = () =>
    $q.when(import('../../../core/dialogs/change-password/change-password-dialog.controller'))
  const getDeleteUserDialogController = () => $q.when(import('../../dialogs/delete-user/delete-user-dialog.controller'))
  const getEditUserBusinessDataFieldsController = () =>
    $q.when(import('../../dialogs/edit-user-business-data-fields/edit-user-business-data-fields-dialog.controller'))
  const getImportUsersDialogController = () =>
    import('../../../admin/dialogs/import-users/import-users-dialog.controller')

  $scope.bUserNotAuthorized = false
  $scope.selectedMenuId = $state.current.data.selectedMenuId
  $scope.loading = true
  $scope.lastRequestId = null
  $scope.userFilters = {
    status: ['ACTIVE', 'PENDING_INVITE'],
    groups: null,
    users: null,
    permissions: null,
    selectedStatusCache: null,
    hideInternalUsers: true,
    search: '',
    internalSearch: false
  }
  $scope.sort = {
    $sort: 'createdAt',
    $order: 'DESC'
  }

  $scope.hasLicenseFeatureEnabled = true
  $scope.activeUsers = null
  $scope.pageTitle = ''

  const api = {
    getEditUserDataFieldController,
    getInviteTeamMembersDialogController,
    getChangePasswordDialogController,
    getDeleteUserDialogController,
    getEditUserBusinessDataFieldsController,
    _getActiveUsers,
    _openInviteTeamMembersDialog,
    _skipPagingTo,
    _handleGetUsersResponse,
    _editUserClick,
    _updateFilters,
    _updateSort,
    _openChangePasswordModal,
    _deleteUser,
    _openBusinessDataFieldsModal,
    _copyInvitationLink,
    _executeCopyLink,
    _showUserQuotaError,
    _successDownloadSpreadsheetUsers,
    _downloadSpreadsheet,
    _showSuccessToast,
    _showErrorToast,
    _addInternalFilter,
    _onSearchUsers: Utils.debounce(_onSearchUsers),
    _setHeader,
    _openImportUsersDialog
  }
  this.getApi = () => api

  function activate () {
    $timeout(() => {
      $rootScope.getUserCompany().then(company => {
        AdminService.getActiveUsers(company.id)
          .then(res => {
            $scope.activeUsers = res
          })
          .catch(handleGetUsersError)
      })
    }, 800)

    Promise.all([
      $rootScope.getUser(),
      $rootScope.getCompanyWithBillingPackage()
    ]).then(([user, companyWithBillingPackage]) => {
      $scope.bUserNotAuthorized = !$rootScope.isUserSysAdminAdmin(user)
      const companyBillingPackage = companyWithBillingPackage?.billingPackage
      const isWorkflow = companyBillingPackage?.plan?.pricingPackage?.id === 4

      api._setHeader(isWorkflow)

      if ($rootScope.isUserSysAdmin(user)) {
        api._addInternalFilter()
      }
    })

    api._skipPagingTo(null, 0)
  }

  $scope.skipPagingTo = api._skipPagingTo

  $scope.tableInfo = {
    tooltip: '',
    emptyMode: {
      title: $translate.instant('noUsersFound'),
      button: {
        text: $translate.instant('inviteUsers'),
        icon: 'user-plus',
        buttonClick: api._openInviteTeamMembersDialog
      }
    },
    columns: [
      { name: $translate.instant('user'), orderable: true, value: 'name' },
      { name: $translate.instant('email'), orderable: false, value: 'email' },
      { name: $translate.instant('permission'), orderable: false, value: 'permission' },
      { name: $translate.instant('groups'), orderable: false, value: 'groups' },
      { name: $translate.instant('status'), orderable: false, value: 'status' }
    ],
    columnsGrid: '4% 22% 25% 12% 17% 14% 6%',
    rows: [],
    editButton: {
      openUpdateUserModal: api._editUserClick,
      openBusinessDataFieldsModal: api._openBusinessDataFieldsModal,
      openChangePasswordModal: api._openChangePasswordModal,
      deleteUser: api._deleteUser,
      copyInvitationLink: api._copyInvitationLink,
      dropdown: true
    },
    updateFilters: api._updateFilters,
    updateSort: api._updateSort,
    refresherCounter: 0,
    paging: {
      top: 16,
      count: 0,
      index: 0,
      onTokenSelect: api._skipPagingTo
    }
  }

  activate()

  $scope.$watchCollection('headerToolbar.filter.params', () => {
    $scope.userFilters = $scope.headerToolbar?.filter?.params

    if (!$scope.userFilters?.status) {
      $scope.tableInfo.rows = []
      $scope.tableInfo.paging.count = 0
    } else {
      api._skipPagingTo(null, 0)
    }
  })

  const deregisterActiveUsersReload = $scope.$on('users.reloadActiveUsers', () => {
    api._getActiveUsers()
  })

  const deregisterAlleUsersReload = $scope.$on('users.reloadAllUsers', samePage => {
    if (samePage) {
      api._skipPagingTo(null, $scope.currentPage)
      return
    }
    api._skipPagingTo(null, 0)
  })

  $scope.$on('$destroy', () => {
    deregisterActiveUsersReload()
    deregisterAlleUsersReload()
  })

  $translate.onReady(() => {
    $scope.pageTitle = $translate.instant('users')
  })

  function _showSuccessToast (label) {
    const isStringLabel = typeof label === 'string'
    const toastLabel = isStringLabel ? label : $translate.instant('successTitle')

    ToastService.showSuccessToast(toastLabel)
  }

  function _showErrorToast (label) {
    const isStringLabel = typeof label === 'string'
    const toastLabel = isStringLabel ? label : $translate.instant('somethingWentWrongTryAgainMsg')

    ToastService.showErrorToast(toastLabel)
  }

  $scope.inviteTeamMembersConfirm = function (isSuccess) {
    if (isSuccess) {
      $scope.$broadcast('filters.updateUserFilters')
      api._getActiveUsers()
      api._skipPagingTo(null, 0)
    }
  }

  function _addInternalFilter () {
    $scope.headerToolbar.filter.items = [...$scope.headerToolbar.filter.items, { id: 5, name: 'USER_INTERNAL', order: 4 }]
  }

  function _openInviteTeamMembersDialog () {
    if ($scope.activeUsers >= $rootScope.oUserCompany.maxLicenses) {
      api._showUserQuotaError()
      return
    }

    getInviteTeamMembersDialogController().then(({ InviteTeamMembersDialogController }) => {
      const inviteTeamMembersDialog = UdsModalService.createModal({
        controller: InviteTeamMembersDialogController,
        modalName: 'inviteTeamMembersDialog',
        onClose: (dialogResponse) => {
          if (dialogResponse) {
            $scope.inviteTeamMembersConfirm(dialogResponse.isSuccess)
          }
        }
      })

      inviteTeamMembersDialog.open()
    })
  }

  function _getActiveUsers () {
    $scope.$broadcast('license-card.update')
  }

  function _handleGetUsersResponse (res) {
    $scope.tableInfo.paging.count = res.count
    $scope.tableInfo.rows = res.rows.map(user => {
      let userActive = false

      if (user.userStatus === 'ACTIVE') {
        userActive = true
      }

      const getNormalizedRoleToTranslate = () => {
        if (user.isManager) return 'manager'
        if (user.isConsultant) return 'consultant'
        return user.role
      }

      return {
        id: user.id,
        active: userActive,
        invite: user.invite,
        userStatus: user.userStatus,
        groups: [...user.groups],
        email: user.email,
        removedAt: user.removedAt,
        values: [
          { type: 'USER', value: user },
          { type: 'TEXT', value: user.email },
          { type: 'TEXT', value: $translate.instant(getNormalizedRoleToTranslate()) },
          { type: 'GROUPS', value: user.groups },
          { type: 'ACTIVE', value: user.userStatus }
        ]
      }
    })

    $scope.loading = false
    $scope.tableInfo.refresherCounter++

    $scope.$broadcast('multiselect-groups.unselect-all')
  }

  function handleGetUsersError (oError) {
    $scope.loading = false
    if (oError && oError.status === 403) {
      $scope.bUserNotAuthorized = true
    }
  }

  function _skipPagingTo (event, token) {
    const requestId = nanoid()
    $scope.lastRequestId = requestId

    token = event ? event.detail.currentPage - 1 : token
    $scope.loading = true
    $scope.currentPage = token
    $scope.tableInfo.paging.index = token

    AdminService.filterUsers(
      $scope.tableInfo.paging.top,
      $scope.tableInfo.paging.top * token,
      $scope.userFilters,
      $scope.sort
    )
      .then((users) => {
        const isLastRequest = requestId === $scope.lastRequestId
        if (isLastRequest) {
          api._handleGetUsersResponse(users)
        }
      })
      .catch(handleGetUsersError)
  }

  function _editUserClick (userId) {
    api.getEditUserDataFieldController().then(({ EditUserDataFieldsDialogController }) => {
      const editUserDataFieldsDialog = UdsModalService.createModal({
        controller: EditUserDataFieldsDialogController,
        modalName: 'editUserDataFieldsDialog',
        data: {
          userId
        },
        onClose: (wasUpdated) => {
          if (wasUpdated) {
            api._skipPagingTo(null, $scope.currentPage)
          }
        }
      })

      editUserDataFieldsDialog.open()
    })
  }

  function _updateFilters (filter, value) {
    if (filter === 'filters') {
      $scope.userFilters.groups = value.groups && value.groups.length ? value.groups : null
      $scope.userFilters.users = value.users && value.users.length ? value.users : null
      $scope.userFilters.permissions = value.permissions && value.permissions.length ? value.permissions : null
      $scope.userFilters.selectedStatusCache =
        value.status && value.status.length ? value.status : ['ACTIVE', 'PENDING_INVITE']
      $scope.userFilters.status = value.status && value.status.length ? value.status : ['ACTIVE', 'PENDING_INVITE']
      $scope.userFilters.internalSearch = value.internalSearch ?? null
    } else if (filter === 'search') {
      $scope.userFilters.search = value
    }

    if (!$scope.userFilters.status) {
      $scope.tableInfo.rows = []
      $scope.tableInfo.paging.count = 0
    } else {
      api._skipPagingTo(null, 0)
    }
  }

  function _updateSort (newSort) {
    $scope.sort = newSort

    api._skipPagingTo(null, $scope.currentPage)
  }

  $scope.resendInvitationMailAllUsers = () => {
    $scope.loading = true
    AdminService.resendAllUsersPending($rootScope.oUserCompany.id)
      .then(api._showSuccessToast)
      .catch(api._showErrorToast)
      .finally(function () {
        $scope.loading = false
      })
  }

  $scope.getFormattedSubtitle = function (quotaValue) {
    if (!quotaValue) {
      return
    }

    if ($scope.activeUsers) {
      return $translate.instant('teamSubtitleQuota', {
        quota: quotaValue,
        active: $scope.activeUsers
      })
    }
  }

  function _copyInvitationLink (userId) {
    $scope.loading = true

    AdminService.getInvitationLink(userId)
      .then(api._executeCopyLink)
      .catch(api._showErrorToast)
      .finally(function () {
        $scope.loading = false
      })
  }

  function _openBusinessDataFieldsModal (userId) {
    api.getEditUserBusinessDataFieldsController().then(({ EditUserBusinessDataFieldsController }) => {
      ModalService.showModal({
        autofocus: false,
        templateId: 'editUserBusinessDataFields.html',
        templateUrl:
          '/admin/dialogs/edit-user-business-data-fields/edit-user-business-data-fields-dialog.html?$$REVISION$$',
        destroyable: true,
        controller: EditUserBusinessDataFieldsController,
        data: {
          oUser: { id: userId, customerCompanyId: $rootScope.oUser.customerCompanyId }
        }
      }).then(function (success) {
        if (success) {
          api._skipPagingTo(null, 0)
        }
      })
    })
  }

  function _openImportUsersDialog () {
    if ($scope.activeUsers >= $rootScope.oUserCompany.maxLicenses) {
      api._showUserQuotaError()
      return
    }

    getImportUsersDialogController().then(({ ImportUsersDialogController }) => {
      const importUsersDialog = UdsModalService.createModal({
        controller: ImportUsersDialogController,
        modalName: 'importUsersDialog'
      })

      importUsersDialog.open()
    })
  }

  function _openChangePasswordModal (userId) {
    api.getChangePasswordDialogController().then(({ ChangePasswordDialogController }) => {
      const changePasswordDialog = UdsModalService.createModal({
        controller: ChangePasswordDialogController,
        modalName: 'changePasswordDialog',
        data: {
          oUser: { id: userId }
        },
        onClose: (info) => {
          if (info && info.success) {
            api._showSuccessToast(info.toastLabel)
          } else if (info) {
            api._showErrorToast(info.toastLabel)
          }
        }
      })

      changePasswordDialog.open()
    })
  }

  function _deleteUser (userId) {
    return AdminService.getUserCompanies(userId).then(res => {
      const companies = res.companies
      if (!companies || !companies.length) {
        return
      }

      if (companies && companies.length === 1) {
        return inactivateUserWithData(userId)
      }

      const environments = companies.reduce((array, company) => {
        if (company.customerCompanyId !== $rootScope.oUser.customerCompanyId) {
          const value = {
            value: company.customerCompanyId,
            label: company.name
          }

          array.push(value)
        }

        return array
      }, [])

      api.getDeleteUserDialogController().then(({ DeleteUserDialogController }) => {
        ModalService.showModal({
          templateId: 'deleteUser.html',
          templateUrl: '/admin/dialogs/delete-user/delete-user-dialog.html?$$REVISION$$',
          destroyable: true,
          controller: DeleteUserDialogController,
          data: {
            user: { id: userId },
            environments: {
              customerCompanyId: '',
              values: environments
            },
            type: 'delete'
          }
        })
          .then(option => {
            if (!option) {
              return
            }

            const exclusionSelected = $scope.chooseTypeDelete(option, userId)

            if (!exclusionSelected) {
              $scope.loading = false
              return
            }

            $scope.chooseTypeDelete(option, userId).catch(api._showErrorToast)
          })
          .catch(api._showErrorToast)
      })
    })
  }

  $scope.chooseTypeDelete = (option, id) => {
    switch (option.selected) {
      case 'thisCompany':
        return AdminService.deleteUserFromCompany(option.customerCompanyId, id).then(() => {
          api._showSuccessToast()
          api._skipPagingTo(null, 0)
        })

      case 'allCompanies':
        return inactivateUserWithData(id)

      default:
    }
  }

  function inactivateUserWithData (id) {
    return AdminService.userHasData(id).then(response => {
      if (response && response.hasData) {
        MessageService.warning({
          title: $translate.instant('inactivateUser'),
          text: $translate.instant('inactivateUserDesc'),
          cancelButton: $translate.instant('cancel'),
          confirmButton: $translate.instant('inactivate'),
          closeOnConfirm: true,
          press: function (shouldInactivate) {
            if (!shouldInactivate) {
              $scope.loading = false
              return
            }

            return AdminService.inactivateUser(id).then(() => {
              $scope.loading = false
              api._showSuccessToast()
              api._getActiveUsers()
              api._skipPagingTo(null, 0)
            })
          }
        })
      } else if (response !== undefined) {
        MessageService.warning({
          title: $translate.instant('warning'),
          text: $translate.instant('areYouSureRemoveUser'),
          cancelButton: $translate.instant('no'),
          confirmButton: $translate.instant('yes'),
          closeOnConfirm: true,
          press: function (shouldDelete) {
            if (!shouldDelete) {
              return
            }

            return AdminService.deleteUser(id).then(() => {
              api._showSuccessToast()
              api._skipPagingTo(null, 0)
            })
          }
        })
      }
    })
  }

  function _executeCopyLink (response) {
    const stateUrl = 'signup'
    const invitationLink = $state.href(stateUrl, { inviteToken: response.inviteToken }, { absolute: true })
    const el = document.createElement('textarea')
    el.value = invitationLink
    el.setAttribute('readonly', '')
    el.style.position = 'absolute'
    el.style.left = '-9999px'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)

    api._showSuccessToast()
  }

  function _showUserQuotaError () {
    MessageService.warning({
      title: $translate.instant('activeUsersQuotaErrorTitle'),
      text: $translate.instant('usersMaximumReached')
    })
  }

  function _downloadSpreadsheet ({ businessUnits }) {
    const filters = {
      ...$scope.userFilters,
      businessUnits
    }

    const exportType = businessUnits ? 'businessunit' : 'user'

    AdminService.exportUsers(filters, $scope.sort)
      .then(uri => api._successDownloadSpreadsheetUsers({ uri, exportType }))
      .catch(api._showErrorToast)
  }

  function _successDownloadSpreadsheetUsers ({ uri, exportType }) {
    if (uri) {
      const link = document.createElement('a')
      link.href = uri
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const downloadSuccessText = $translate.instant('downloadSuccessText')
    const requestExcelSuccessText = $translate.instant('requestExcelSuccessText')

    api._showSuccessToast(uri ? downloadSuccessText : requestExcelSuccessText)

    SegmentService.track('general-usermanagement-data-export', {
      type: exportType
    })
  }

  function _onSearchUsers (term) {
    api._updateFilters('search', term)
  }

  function _setHeader (isWorkflow) {
    const userPermissionSlug = isWorkflow ? 'USER_PERMISSION_WORKFLOW' : 'USER_PERMISSION'

    if ($rootScope.isDesktop) {
      $scope.headerToolbar = {
        rightButtons: [{
          type: 'button',
          btnType: 'primary',
          icon: 'user-plus',
          label: $translate.instant('inviteUsers'),
          onClick: api._openInviteTeamMembersDialog
        }, {
          type: 'dropdown',
          btnType: 'icon',
          icon: 'more',
          options: [{
            label: $translate.instant('exportUsers'),
            icon: 'download',
            onClick: api._downloadSpreadsheet.bind(null, { businessUnits: false })
          }, {
            label: $translate.instant('exportBusinessUnits'),
            icon: 'download',
            onClick: api._downloadSpreadsheet.bind(null, { businessUnits: true })
          }]
        }],
        search: {
          onSearch: api._onSearchUsers
        },
        filter: {
          params: {
            users: [],
            groups: [],
            status: [],
            permissions: [],
            internalSearch: false
          },
          items: [
            { id: 3, name: 'USER_STATUS', order: 1 },
            { id: 4, name: userPermissionSlug, order: 2 },
            { id: 2, name: 'GROUPS', order: 3 }
          ]
        }
      }

      if ($rootScope.isUserSysAdmin()) {
        $scope.headerToolbar.rightButtons.unshift({
          type: 'button',
          btnType: 'secondary',
          icon: 'plus',
          label: $translate.instant('importUsers'),
          onClick: api._openImportUsersDialog
        })
      }
    } else {
      $scope.headerToolbar = {
        rightButtons: [{
          type: 'button',
          btnType: 'primary',
          icon: 'user-plus',
          label: $translate.instant('invite'),
          onClick: api._openInviteTeamMembersDialog
        }],
        search: {
          onSearch: api._onSearchUsers
        }
      }
    }
  }
}

export { UsersController }
